import { EntityIcon } from '@common/Entity/EntityIcons';
import { Entity } from '@common/types/Entity';
import { renderColumnWithEllipsis } from '@common/utils/renderUtils';
import { useStoreProceduresData } from '@pages/Transpiler/useStoreProceduresData';
import { theme } from '@prophecy/theme';
import { ColumnsType, Stack, Table } from '@prophecy/ui-v3';
import React, { useMemo } from 'react';

import { SourceInfoType } from '../../../../data/apis/transpileTypes';
import { Error } from '../../Import/utils';
import { getStatusColumn, InfoTableHeader, StyledNameContainer } from '../util';

export const StoredProceduresTable = ({
  isBulk,
  id
}: {
  projectName?: string;
  isBulk: boolean;
  id: string;
  isSql?: boolean;
  projectId?: string;
}) => {
  const spColumns: ColumnsType<SourceInfoType> = [
    {
      key: 'entityName',
      dataIndex: 'entityName',
      title: 'Name',
      filter: {
        type: 'search',
        searchInputProps: {
          placeholder: 'Search..',
          'data-test-id': 'stored-procedures-table-name-search-input'
        }
      },
      render: (name) => {
        return (
          <Stack direction='horizontal' gap={theme.spaces.x4} width='100%' alignY='center'>
            <EntityIcon entity={Entity.StoredProcedure} size='xs' />
            <StyledNameContainer grow='1'>{renderColumnWithEllipsis(name)}</StyledNameContainer>
          </Stack>
        );
      }
    },
    {
      key: 'parentEntity',
      dataIndex: 'parentEntity',
      title: 'Parent Entity',
      render: renderColumnWithEllipsis,
      sortable: true
    },
    {
      key: 'source',
      dataIndex: 'source',
      title: 'Source File',
      render: renderColumnWithEllipsis,
      sortable: true
    },
    {
      key: 'description',
      dataIndex: 'description',
      title: 'Message',
      render: (msg) => renderColumnWithEllipsis(msg, undefined, undefined, true)
    },
    getStatusColumn()
  ];

  const { data, isError, isLoading } = useStoreProceduresData(id, !isBulk);

  const storedProcedures = useMemo(() => data?.data?.storedProcedures || [], [data?.data]);

  if (isError) {
    return <Error message='Some error occurred' />;
  }

  return (
    <Stack width='100%' height='100%' gap={theme.spaces.x12}>
      <InfoTableHeader title='Stored Procedures' count={storedProcedures.length} />
      <Stack grow='1'>
        <Table
          fixedHeader={true}
          height='100%'
          loading={isLoading}
          width='100%'
          virtualize={true}
          rowKey={'rowKey'}
          columns={spColumns}
          data-test-id='imported-info-stored-procedures-table'
          dataSource={storedProcedures.map((udf, i) => ({ ...udf, rowKey: i }))}
        />
      </Stack>
    </Stack>
  );
};
